/**
 * The typed faces people have been sending since long before there were emojis -- ";)" becomes 😉.
 *
 * Kept to the ones that can't be mistaken for ordinary punctuation.  ":)" only ever means a smile,
 * but "8)" is as likely to be the eighth item in a list as a pair of sunglasses, so the sunglasses
 * are spelled "8-)" here and a numbered list is left alone.  A face is only recognised standing on
 * its own, too: "http://x" and "ratio 3:4" keep their colons.
 *
 * The emoji each one maps to is one the picker already offers, so every emoji in a message is one
 * the reader could also have found by hand.
 */
export interface IEmoticon {
	/** Typed exactly like this, case included -- ":D" is a grin, ":d" is somebody's initials. */
	text: string;
	emoji: string;
}

export const EMOTICONS: IEmoticon[] = [
	{ text: ":)", emoji: "🙂" },
	{ text: ":-)", emoji: "🙂" },
	{ text: "=)", emoji: "🙂" },
	{ text: ":D", emoji: "😄" },
	{ text: ":-D", emoji: "😄" },
	{ text: "=D", emoji: "😄" },
	{ text: ";)", emoji: "😉" },
	{ text: ";-)", emoji: "😉" },
	{ text: ":P", emoji: "😜" },
	{ text: ":-P", emoji: "😜" },
	{ text: ":p", emoji: "😜" },
	{ text: ":-p", emoji: "😜" },
	{ text: ":(", emoji: "🙁" },
	{ text: ":-(", emoji: "🙁" },
	{ text: "=(", emoji: "🙁" },
	{ text: ":'(", emoji: "😢" },
	{ text: ":O", emoji: "😯" },
	{ text: ":-O", emoji: "😯" },
	{ text: ":o", emoji: "😯" },
	{ text: ":-o", emoji: "😯" },
	{ text: ":/", emoji: "😕" },
	{ text: ":-/", emoji: "😕" },
	{ text: ":*", emoji: "😗" },
	{ text: ":-*", emoji: "😗" },
	{ text: "8-)", emoji: "😎" },
	{ text: "B-)", emoji: "😎" },
	{ text: ">:(", emoji: "😠" },
	{ text: "<3", emoji: "❤️" },
	{ text: "</3", emoji: "💔" }
];

/**
 * The faces as one alternation, longest first so ":-)" is recognised as itself rather than as a ":-"
 * followed by a ")" that never matches.  Built once: it's the same pattern every keystroke.
 */
const EMOTICON_PATTERN = EMOTICONS
	.map(x => x.text)
	.sort((a, b) => b.length - a.length)
	.map(x => x.replace(/[.*+?^${}()|[\]\\\/]/g, "\\$&"))
	.join("|");

/** A face standing on its own somewhere in a line -- used to sweep a finished message. */
export const EMOTICON_REGEX = new RegExp(`(^|\\s)(${EMOTICON_PATTERN})(?=\\s|$)`, "g");

/** A face standing on its own at the very end of what's been typed so far. */
export const EMOTICON_END_REGEX = new RegExp(`(^|\\s)(${EMOTICON_PATTERN})$`);

/** The emoji for an exact face, or null if it isn't one. */
export function getEmoticonEmoji(text: string): string {
	const emoticon = EMOTICONS.find(x => x.text === text);

	return emoticon ? emoticon.emoji : null;
}
